import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function CinewallOfferte() {
  const [formData, setFormData] = useState({
    naam: '',
    email: '',
    telefoon: '',
    woonplaats: '',
    typeWoning: '',
    afmetingen: '',
    tvFormaat: '',
    haardKeuze: '',
    ledNissen: '',
    budget: '',
    bericht: '',
    fotoUpload: [] as File[]
  });

  const [errors, setErrors] = useState({
    naam: '',
    email: '',
    telefoon: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [uploadError, setUploadError] = useState('');

  useSEO({
    title: 'Offerte aanvragen | Cinewall op maat laten maken',
    description: 'Vraag een vrijblijvende offerte aan voor uw cinewall op maat. Vul het formulier in en ontvang binnen 24 uur persoonlijk advies en een prijsindicatie.',
    keywords: 'cinewall offerte, cinewall aanvragen, cinewall prijs, offerte op maat',
    canonical: `${import.meta.env.VITE_SITE_URL}/cinewall-offerte`,
  });

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone: string) => {
    const cleaned = phone.replace(/\s/g, '');
    return cleaned.length >= 8 && /^[0-9+\-()]+$/.test(cleaned);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    setUploadError('');
    const validFiles: File[] = [];
    const maxSize = 10 * 1024 * 1024; // 10 MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'];

    // Check existing files count
    const currentCount = formData.fotoUpload.length;
    const newFilesArray = Array.from(files);

    if (currentCount + newFilesArray.length > 3) {
      setUploadError('U kunt maximaal 3 foto\'s uploaden');
      return;
    }

    for (const file of newFilesArray) {
      // Check file type
      if (!allowedTypes.includes(file.type.toLowerCase())) {
        setUploadError(`${file.name}: alleen JPG, JPEG, PNG en HEIC zijn toegestaan`);
        continue;
      }

      // Check file size
      if (file.size > maxSize) {
        setUploadError(`${file.name}: bestand is te groot (max 10 MB)`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setFormData(prev => ({ ...prev, fotoUpload: [...prev.fotoUpload, ...validFiles] }));
    }

    // Reset input
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    const newFiles = formData.fotoUpload.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, fotoUpload: newFiles }));
    setUploadError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const newErrors = {
      naam: '',
      email: '',
      telefoon: ''
    };

    if (!formData.naam.trim()) {
      newErrors.naam = 'Vul uw naam in';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Vul uw e-mailadres in om uw aanvraag te versturen';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Vul een geldig e-mailadres in';
    }

    if (!formData.telefoon.trim()) {
      newErrors.telefoon = 'Vul uw telefoonnummer in om uw aanvraag te versturen';
    } else if (!validatePhone(formData.telefoon)) {
      newErrors.telefoon = 'Vul een geldig telefoonnummer in';
    }

    if (newErrors.naam || newErrors.email || newErrors.telefoon) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const now = new Date();
      const datumIngediend = now.toLocaleDateString('nl-NL');
      const tijdIngediend = now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
      
      // Convert files to base64 for webhook
      const filesBase64 = await Promise.all(
        formData.fotoUpload.map(async (file) => {
          return new Promise<{ name: string; type: string; size: number; data: string }>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve({
                name: file.name,
                type: file.type,
                size: file.size,
                data: reader.result as string
              });
            };
            reader.readAsDataURL(file);
          });
        })
      );

      // Prepare webhook payload
      const webhookPayload = {
        form_type: 'offerte',
        form_name: 'Offerte aanvraag',
        page_url: '/cinewall-offerte',
        datum_ingediend: datumIngediend,
        tijd_ingediend: tijdIngediend,
        timestamp: now.toISOString(),
        
        // Contact info
        naam: formData.naam,
        email: formData.email,
        telefoon: formData.telefoon,
        
        // Project details
        woonplaats: formData.woonplaats || '-',
        type_woning: formData.typeWoning || '-',
        breedte_cinewall: formData.afmetingen || '-',
        hoogte_cinewall: '-',
        tv_formaat: formData.tvFormaat || '-',
        soundbar: '-',
        haard_type: formData.haardKeuze || '-',
        nissen_aantal: '-',
        led_nissen: formData.ledNissen || '-',
        kastruimte_links: '-',
        kastruimte_rechts: '-',
        kleurpalet: '-',
        
        // Budget & remarks
        indicatie_prijs: formData.budget || '-',
        opmerkingen: formData.bericht || '-',
        
        // Files
        fotos: filesBase64,
        aantal_fotos: filesBase64.length,
        
        // Source
        bron: 'Offertepagina'
      };

      console.log('🚀 Sending to Make.com webhook...', {
        url: 'https://hook.eu1.make.com/ph2tsrq88dy9uurcqxt423mg5tx39lbf',
        payload: webhookPayload
      });

      // Send to Make.com webhook - WAIT FOR COMPLETION
      const webhookResponse = await fetch('https://hook.eu1.make.com/ph2tsrq88dy9uurcqxt423mg5tx39lbf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload),
      });

      console.log('✅ Webhook response:', {
        status: webhookResponse.status,
        statusText: webhookResponse.statusText,
        ok: webhookResponse.ok
      });

      if (!webhookResponse.ok) {
        const errorText = await webhookResponse.text();
        console.error('❌ Webhook failed:', errorText);
        throw new Error(`Webhook failed: ${webhookResponse.status}`);
      }

      console.log('✅ Webhook succeeded!');

      // Success!
      setSubmitSuccess(true);
      setFormData({
        naam: '',
        email: '',
        telefoon: '',
        woonplaats: '',
        typeWoning: '',
        afmetingen: '',
        tvFormaat: '',
        haardKeuze: '',
        ledNissen: '',
        budget: '',
        bericht: '',
        fotoUpload: []
      });
      
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
      console.error('❌ Submit error:', error);
      setSubmitError('Er is iets misgegaan bij het versturen. Probeer het opnieuw of neem contact met ons op.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F6F3] flex flex-col">
      <Navbar />
      
      <main className="flex-grow pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-light text-[#0F0F0F] mb-4 tracking-wide">
              Vraag uw offerte aan
            </h1>
            <p className="text-lg text-[#4A4A4A] max-w-2xl mx-auto leading-relaxed">
              Vul onderstaand formulier in en ontvang binnen 24 uur een vrijblijvende prijsindicatie op maat.
            </p>
          </div>

          {/* Success Message */}
          {submitSuccess && (
            <div className="mb-8 p-6 bg-[#C7A86A]/10 border border-[#C7A86A]/30 rounded-lg">
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-2xl text-[#C7A86A]"></i>
                <div>
                  <h3 className="text-lg font-semibold text-[#0F0F0F] mb-2">
                    Dank voor uw aanvraag
                  </h3>
                  <p className="text-[#4A4A4A] leading-relaxed">
                    Wij nemen binnen 24 uur contact met u op.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {submitError && (
            <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <i className="ri-error-warning-line text-2xl text-red-600"></i>
                <p className="text-red-800">{submitError}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form 
            onSubmit={handleSubmit} 
            className="bg-white rounded-lg shadow-sm p-8 md:p-12"
          >
            {/* Required Fields Section */}
            <div className="mb-10">
              <h2 className="text-xl font-semibold text-[#0F0F0F] mb-6 flex items-center gap-2">
                Contactgegevens
                <span className="text-sm font-normal text-[#C7A86A]">* Verplicht</span>
              </h2>
              
              <div className="space-y-6">
                {/* Naam */}
                <div>
                  <label htmlFor="naam" className="block text-sm font-medium text-[#0F0F0F] mb-2">
                    Naam *
                  </label>
                  <input
                    type="text"
                    id="naam"
                    name="naam"
                    value={formData.naam}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border ${errors.naam ? 'border-red-400' : 'border-[#D4D4D4]'} rounded-md focus:outline-none focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent transition-all text-[#0F0F0F]`}
                    placeholder="Uw volledige naam"
                  />
                  {errors.naam && (
                    <p className="mt-2 text-sm text-red-600">{errors.naam}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#0F0F0F] mb-2">
                    E-mailadres *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border ${errors.email ? 'border-red-400' : 'border-[#D4D4D4]'} rounded-md focus:outline-none focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent transition-all text-[#0F0F0F]`}
                    placeholder="uw@email.nl"
                  />
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                {/* Telefoon */}
                <div>
                  <label htmlFor="telefoon" className="block text-sm font-medium text-[#0F0F0F] mb-2">
                    Telefoonnummer *
                  </label>
                  <input
                    type="tel"
                    id="telefoon"
                    name="telefoon"
                    value={formData.telefoon}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border ${errors.telefoon ? 'border-red-400' : 'border-[#D4D4D4]'} rounded-md focus:outline-none focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent transition-all text-[#0F0F0F]`}
                    placeholder="06 12345678"
                  />
                  {errors.telefoon && (
                    <p className="mt-2 text-sm text-red-600">{errors.telefoon}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Optional Fields Section */}
            <div className="mb-10">
              <h2 className="text-xl font-semibold text-[#0F0F0F] mb-6">
                Projectgegevens <span className="text-sm font-normal text-[#6B6B6B]">(optioneel)</span>
              </h2>
              
              <div className="space-y-6">
                {/* Woonplaats */}
                <div>
                  <label htmlFor="woonplaats" className="block text-sm font-medium text-[#0F0F0F] mb-2">
                    Woonplaats
                  </label>
                  <input
                    type="text"
                    id="woonplaats"
                    name="woonplaats"
                    value={formData.woonplaats}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#D4D4D4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent transition-all text-[#0F0F0F]"
                    placeholder="Uw woonplaats"
                  />
                </div>

                {/* Type Woning */}
                <div>
                  <label htmlFor="typeWoning" className="block text-sm font-medium text-[#0F0F0F] mb-2">
                    Type woning
                  </label>
                  <select
                    id="typeWoning"
                    name="typeWoning"
                    value={formData.typeWoning}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#D4D4D4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent transition-all text-[#0F0F0F] cursor-pointer"
                  >
                    <option value="">Selecteer type woning</option>
                    <option value="Appartement">Appartement</option>
                    <option value="Tussenwoning">Tussenwoning</option>
                    <option value="Hoekwoning">Hoekwoning</option>
                    <option value="Vrijstaand">Vrijstaand</option>
                    <option value="Villa">Villa</option>
                    <option value="Anders">Anders</option>
                  </select>
                </div>

                {/* Afmetingen */}
                <div>
                  <label htmlFor="afmetingen" className="block text-sm font-medium text-[#0F0F0F] mb-2">
                    Gewenste afmetingen cinewall
                  </label>
                  <input
                    type="text"
                    id="afmetingen"
                    name="afmetingen"
                    value={formData.afmetingen}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#D4D4D4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent transition-all text-[#0F0F0F]"
                    placeholder="Bijvoorbeeld: 4 meter breed x 2,5 meter hoog"
                  />
                </div>

                {/* TV Formaat */}
                <div>
                  <label htmlFor="tvFormaat" className="block text-sm font-medium text-[#0F0F0F] mb-2">
                    TV formaat
                  </label>
                  <select
                    id="tvFormaat"
                    name="tvFormaat"
                    value={formData.tvFormaat}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#D4D4D4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent transition-all text-[#0F0F0F] cursor-pointer"
                  >
                    <option value="">Selecteer TV formaat</option>
                    <option value="55 inch">55 inch</option>
                    <option value="65 inch">65 inch</option>
                    <option value="75 inch">75 inch</option>
                    <option value="85 inch">85 inch</option>
                    <option value="Nog niet bekend">Nog niet bekend</option>
                  </select>
                </div>

                {/* Haard Keuze */}
                <div>
                  <label htmlFor="haardKeuze" className="block text-sm font-medium text-[#0F0F0F] mb-2">
                    Haard keuze
                  </label>
                  <select
                    id="haardKeuze"
                    name="haardKeuze"
                    value={formData.haardKeuze}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#D4D4D4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent transition-all text-[#0F0F0F] cursor-pointer"
                  >
                    <option value="">Selecteer haard keuze</option>
                    <option value="Geen haard">Geen haard</option>
                    <option value="Elektrische haard">Elektrische haard</option>
                    <option value="Waterdamp haard">Waterdamp haard</option>
                  </select>
                </div>

                {/* LED Nissen */}
                <div>
                  <label htmlFor="ledNissen" className="block text-sm font-medium text-[#0F0F0F] mb-2">
                    LED nissen
                  </label>
                  <select
                    id="ledNissen"
                    name="ledNissen"
                    value={formData.ledNissen}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#D4D4D4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent transition-all text-[#0F0F0F] cursor-pointer"
                  >
                    <option value="">Selecteer optie</option>
                    <option value="Ja">Ja, LED nissen gewenst</option>
                    <option value="Nee">Nee, geen LED nissen</option>
                  </select>
                </div>

                {/* Budget */}
                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-[#0F0F0F] mb-2">
                    Budgetindicatie
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#D4D4D4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent transition-all text-[#0F0F0F] cursor-pointer"
                  >
                    <option value="">Selecteer budget</option>
                    <option value="€ 5.000 - € 10.000">€ 5.000 - € 10.000</option>
                    <option value="€ 10.000 - € 15.000">€ 10.000 - € 15.000</option>
                    <option value="€ 15.000 - € 20.000">€ 15.000 - € 20.000</option>
                    <option value="€ 20.000+">€ 20.000+</option>
                  </select>
                </div>

                {/* Photo Upload */}
                <div>
                  <label className="block text-sm font-semibold text-[#0F0F0F] mb-2">
                    Upload foto van de muur <span className="text-gray-400 font-normal">(optioneel)</span>
                  </label>
                  <p className="text-sm text-gray-600 mb-3">
                    Een foto helpt ons om sneller en gerichter te adviseren.
                  </p>
                  
                  {/* File Upload Input */}
                  <div className="relative">
                    <input
                      type="file"
                      id="fotoUpload"
                      accept="image/jpeg,image/jpg,image/png,image/heic"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={formData.fotoUpload.length >= 3}
                    />
                    <label
                      htmlFor="fotoUpload"
                      className={`flex items-center justify-center w-full px-4 py-6 border-2 border-dashed rounded-lg transition-all cursor-pointer ${
                        formData.fotoUpload.length >= 3
                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                          : 'border-gray-300 hover:border-[#C7A86A] bg-white'
                      }`}
                    >
                      <div className="text-center">
                        <i className="ri-upload-cloud-2-line text-3xl text-gray-400 mb-2"></i>
                        <p className="text-sm text-gray-600">
                          {formData.fotoUpload.length >= 3
                            ? 'Maximaal 3 foto\'s bereikt'
                            : 'Klik om foto\'s te uploaden'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          JPG, JPEG, PNG, HEIC (max 10 MB per foto, max 3 foto's)
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Upload Error */}
                  {uploadError && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600 flex items-start">
                        <i className="ri-error-warning-line mr-2 mt-0.5 flex-shrink-0"></i>
                        {uploadError}
                      </p>
                    </div>
                  )}

                  {/* File Preview */}
                  {formData.fotoUpload.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-semibold text-gray-700">
                        Geüploade foto's ({formData.fotoUpload.length}/3):
                      </p>
                      <div className="grid grid-cols-1 gap-3">
                        {formData.fotoUpload.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg border border-stone-200 flex-shrink-0">
                                <i className="ri-image-line text-xl text-[#C7A86A]"></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {file.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="ml-3 w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg transition-all flex-shrink-0"
                              aria-label="Verwijder foto"
                            >
                              <i className="ri-delete-bin-line text-lg"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Bericht */}
                <div>
                  <label htmlFor="bericht" className="block text-sm font-medium text-[#0F0F0F] mb-2">
                    Aanvullende wensen of opmerkingen
                  </label>
                  <textarea
                    id="bericht"
                    name="bericht"
                    value={formData.bericht}
                    onChange={handleChange}
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-[#D4D4D4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#C7A86A] focus:border-transparent transition-all text-[#0F0F0F] resize-none"
                    placeholder="Vertel ons meer over uw wensen..."
                  />
                  <p className="mt-2 text-sm text-[#6B6B6B]">
                    Maximaal 500 tekens ({formData.bericht.length}/500)
                  </p>
                </div>
              </div>
            </div>

            {/* Trust Elements */}
            <div className="mb-8 p-6 bg-[#F8F6F3] rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-xl text-[#C7A86A] mt-0.5"></i>
                  <div>
                    <p className="text-sm font-medium text-[#0F0F0F]">Persoonlijk contact</p>
                    <p className="text-xs text-[#6B6B6B]">Direct contact met onze specialist</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-time-line text-xl text-[#C7A86A] mt-0.5"></i>
                  <div>
                    <p className="text-sm font-medium text-[#0F0F0F]">Reactie binnen 24 uur</p>
                    <p className="text-xs text-[#6B6B6B]">Snelle en professionele service</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-shield-check-line text-xl text-[#C7A86A] mt-0.5"></i>
                  <div>
                    <p className="text-sm font-medium text-[#0F0F0F]">Vrijblijvend</p>
                    <p className="text-xs text-[#6B6B6B]">Geen verplichtingen</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-star-line text-xl text-[#C7A86A] mt-0.5"></i>
                  <div>
                    <p className="text-sm font-medium text-[#0F0F0F]">Specialist in luxe cinewalls</p>
                    <p className="text-xs text-[#6B6B6B]">Ontwerp, productie & montage</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#C7A86A] text-[#0F0F0F] px-8 py-4 rounded-md font-semibold hover:bg-[#B89858] transition-all duration-300 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? 'Bezig met versturen...' : 'Verstuur aanvraag'}
            </button>
          </form>

          {/* What Happens Next */}
          <div className="mt-12 text-center">
            <h3 className="text-xl font-semibold text-[#0F0F0F] mb-6">
              Wat gebeurt er na uw aanvraag?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-[#C7A86A] rounded-full flex items-center justify-center text-[#0F0F0F] font-bold mb-3">
                  1
                </div>
                <p className="text-sm text-[#4A4A4A]">U ontvangt direct een bevestiging per e-mail</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-[#C7A86A] rounded-full flex items-center justify-center text-[#0F0F0F] font-bold mb-3">
                  2
                </div>
                <p className="text-sm text-[#4A4A4A]">Wij nemen binnen 24 uur telefonisch contact op</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-[#C7A86A] rounded-full flex items-center justify-center text-[#0F0F0F] font-bold mb-3">
                  3
                </div>
                <p className="text-sm text-[#4A4A4A]">Samen bespreken we uw wensen en mogelijkheden</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-[#C7A86A] rounded-full flex items-center justify-center text-[#0F0F0F] font-bold mb-3">
                  4
                </div>
                <p className="text-sm text-[#4A4A4A]">U ontvangt een vrijblijvende offerte op maat</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
